import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { BrandRingMark } from './BrandRingMark.js';
/**
 * A full-screen overlay with the loader centred on screen: fixed, inset 0, a see-through backdrop
 * in the theme's background colour (light and dark), above everything and blocking clicks.
 * It renders into document.body once mounted, so a blurred or transformed card can't trap it.
 * Focus moves to the overlay (Tab can't reach the page underneath) and goes back to where it
 * was when the overlay closes. `children` is the app's own loader; without it, the brand ring.
 */
const noSubscription = () => () => undefined;
const bodyElement = () => document.body;
const noBody = () => null;
export function LoaderOverlay({ label, children }) {
    const ref = useRef(null);
    // document.body in the browser; null on the server, where it renders in place.
    const host = useSyncExternalStore(noSubscription, bodyElement, noBody);
    useEffect(() => {
        const overlay = ref.current;
        if (!overlay)
            return undefined;
        const before = document.activeElement;
        overlay.focus({ preventScroll: true });
        return () => {
            const lost = document.activeElement === overlay || document.activeElement === document.body;
            if (lost && before instanceof HTMLElement && before.isConnected)
                before.focus({ preventScroll: true });
        };
    }, [host]);
    const keepFocus = (event) => {
        if (event.key === 'Tab')
            event.preventDefault();
    };
    const overlay = (_jsx("div", { ref: ref, className: "loader-overlay", "data-loader-overlay": "", "aria-busy": "true", "aria-label": label, tabIndex: -1, onKeyDown: keepFocus, onClick: (event) => event.stopPropagation(), children: children ?? _jsx(OverlayMark, { label: label }) }));
    return host ? createPortal(overlay, host) : overlay;
}
/** The default overlay content: the brand ring and the label, read out politely. */
function OverlayMark({ label }) {
    return (_jsxs("span", { className: "loader-overlay__panel", role: "status", "aria-live": "polite", children: [_jsx(BrandRingMark, { block: "loader-overlay", badgeSize: 32 }), _jsx("span", { className: "loader-overlay__text", children: label })] }));
}
//# sourceMappingURL=LoaderOverlay.js.map